import { KeyboardEvent, MutableRefObject, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, ButtonSizeLG, ButtonSizeSM, ButtonSkinPrimary, ButtonSkinSecondary, ButtonSkinTertiary, ButtonVariants } from '@picsart/button';
import { getContext } from '@picsart/miniapps-sdk';
import { Text, TypographyHorizontalAlign } from '@picsart/text';
import { FontWeights, Spacings, Statuses, Texts, Typography } from '@picsart/cascade';
import { useTranslator } from '@picsart/rc/services/localization';
import { ScrollBoxVariants } from '@picsart/scrollbox';
import ScrollBox from '@picsart/scrollbox/ScrollBox';
import Image from '@picsart/image/Image';
import { View } from '@picsart/view';
import { ImageFrame } from '@picsart/imageframe';
import { Grid } from '@picsart/grid';
import IconInfoCircleOutline from '@picsart/icons/IconInfoCircleOutline';
import IconCrossLarge from '@picsart/icons/IconCrossLarge';
import IconCrossCircleFilled from '@picsart/icons/IconCrossCircleFilled';
import { ControlSizesXL, TextArea, TextAreaStatuses } from '@picsart/textarea';
import Pigment from '@picsart/pigment/Pigment';
import { InlineButton } from '@picsart/inlineButton';
import { Link } from '@picsart/link';
import { v4 as uuid } from 'uuid';
import { createReplayImageLayer, findReplayGroups } from '@picsart/web-replay-sdk/context';
import { DAILY_ALLOWANCE, defaultConfig, State } from '../../constants';
import allowanceReached from '../../utils/allowenceReached';
import { decreaseAllowance } from '../../utils/decreaseAllowance';
import { PromptError } from '../../utils/errors/promptError';
import { NsfwError } from '../../utils/errors/nsfwError';
import updateImageVisibility from '../../utils/updateImageVisibility';
import Results from '../Results';
import Error from '../Error';
import Layout, { Body, Footer, Header } from '../Layout';
import { IStyle, ResultImage } from '../../types';
import getTaskResult from '../../utils/getTaskResult';
import useStyles from './useStyles';
import publishRegistrationEvent from '../../utils/publishRegistrationEvent';
import requestTextToBG from '../../utils/requestTextToBG';

// TODO: not free case
const {
  entry_page_config,
  result_page_config,
  service_config,
  usage_limitation_config: { free },
} = defaultConfig;

function Main() {
  const classes = useStyles();

  const context = getContext();

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [promptId, setPromptId] = useState('');

  const { pageConfig, resultPageConfig, serviceConfig } = useMemo(() => {
    const { default_config, defaultConfigs } = context.general.location.getState().params;

    try {
      const config = JSON.parse((default_config || defaultConfigs) as string);
      const _pageConfig = {
        ...entry_page_config,
        ...config.entry_page_config,
      };

      return {
        pageConfig: _pageConfig,
        serviceConfig: {
          ...service_config,
          ...config.service_config,
        },
        resultPageConfig: {
          ...result_page_config,
          ...config.result_page_config,
          title: _pageConfig.title,
        },
      };
    } catch (err) {
      return {
        pageConfig: {
          ...entry_page_config,
        },
        serviceConfig: {
          ...service_config,
        },
        resultPageConfig: {
          ...result_page_config,
          title: entry_page_config.title,
        },
      };
    }
  }, [context.general.location]);

  const [appState, setAppState] = useState<State>(State.OPTIONS);
  const [text, setText] = useState((context.general.location.getState().params.search as string) || '');
  const [colors, setColors] = useState<string[]>([]);
  const isGenerateButtonClicked = useRef(false);
  const translate = useTranslator();

  const { terms_of_services_key: termsOfServicesKey, count_check_for_terms: countCheckForTerms } = pageConfig;
  const { getState, setState } = getContext().internals;
  const state = getState({ permanent: true });
  state[termsOfServicesKey] ||= [];
  const isTermsOfUseShown = state[termsOfServicesKey].length < countCheckForTerms;

  const [style, setStyle] = useState<IStyle | null>(null);

  const [images, setImages] = useState<ResultImage[]>([]);
  const [limitReached, setLimitReached] = useState(allowanceReached(context));

  const [promptError, setPromptError] = useState<null | string>(null);
  const [error, setError] = useState<null | string>(null);
  const errorRetryAction = useRef<null | (() => void)>(null);

  const [mainHeight, setMainHeight] = useState<number>(window.innerHeight);

  const hasVisibility: MutableRefObject<ResultImage[]> = useRef([]);
  const { params: deeplink } = getContext().general.location.getState();

  // For deeplinks
  useEffect(() => {
    const { prompt: deeplinkText } = deeplink;

    if (deeplinkText) setText(deeplinkText as string);
  }, []);

  useEffect(() => {
    const handleWindowResize = () => {
      setMainHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  // Run this on each rerender to make sure we display the error message
  if (allowanceReached(context) && !limitReached) {
    setLimitReached(true);
  } else if (!allowanceReached(context) && limitReached) {
    setLimitReached(false);
  }

  // Run this when user want to edit description
  const edit = useCallback(() => {
    setImages([]);
    setAppState(State.OPTIONS);
    setError(null);
    setPromptError(null);
    hasVisibility.current = [];
  }, []);

  const handleError = useCallback(
    (err: Error, retryAction: () => void) => {
      context.handlers.raiseError(err.message);

      if (err instanceof PromptError) {
        setAppState(State.OPTIONS);
        setImages([]);
        setPromptError(err.message);
        return;
      }

      if (err instanceof NsfwError) {
        setAppState(State.OPTIONS);
        setImages([]);
        setText(pageConfig.nsfw_stop_info);
        setPromptError(err.message);
        return;
      }

      setError(err.message);

      errorRetryAction.current = () => {
        errorRetryAction.current = null;
        setError(null);

        retryAction();
      };
    },
    [context.handlers, pageConfig.nsfw_stop_info],
  );

  const addImageToCanvas = useCallback(
    async (images2: ResultImage[]) => {
      const visibilityImages = [];

      // eslint-disable-next-line no-restricted-syntax
      for await (const image of images2) {
        try {
          if (!hasVisibility.current.includes(image)) {
            await updateImageVisibility(serviceConfig, image.id, promptId);
            hasVisibility.current.push(image);
          }

          visibilityImages.push(image);
        } catch (e) {
          console.log('Visibility or publish problem');
        }
      }

      const layerId = uuid();
      const resourceUrl = visibilityImages[0]?.url;
      const resourseId = visibilityImages[0]?.id;

      const { scene } = getContext();

      const replayImageLayer = createReplayImageLayer({
        name: 'Background image',
        uuid: layerId,
        locked: false,
        fallbackLayer: resourseId,
        meta: {},
        mediaType: 'BACKGROUND_IMAGE',
        props: {
          hidden: false,
          opacity: 1,
          rotation: 0,
          image: {
            width: 1024,
            height: 1024,
            location: resourceUrl,
            type: 'photo',
            id: resourseId,
          },
          scaleX: scene.replay.context.layout.width / 1024,
          scaleY: scene.replay.context.layout.height / 1024,
        },
      });
      scene.updateReplay((draftReplay: any) => {
        const groups = findReplayGroups(draftReplay);
        const canvasGroup = groups.find(group => context.scene.hasLayerMediaType(group, 'CANVAS_GROUP'));
        if (canvasGroup) {
          const existingBackgroundLayerIndex = canvasGroup.props.children.value.findIndex((layerUuid: string) => {
            const layer = draftReplay.context.layers[layerUuid];
            return layer && (context.scene.hasLayerMediaType(layer, 'BACKGROUND_IMAGE') || context.scene.hasLayerMediaType(layer, 'BACKGROUND_COLOR'));
          });
          if (existingBackgroundLayerIndex !== -1) {
            canvasGroup.props.children.value[existingBackgroundLayerIndex] = replayImageLayer.uuid;
          } else {
            canvasGroup.props.children.value.unshift(replayImageLayer.uuid);
          }
          // eslint-disable-next-line no-param-reassign
          draftReplay.context.layers[replayImageLayer.uuid] = replayImageLayer;
        } else {
          console.error('Canvas group not found');
        }
      });
      await scene.sync();
    },
    [promptId, serviceConfig, context.scene],
  );

  const loadImages = useCallback(
    async (append = false) => {
      if (limitReached) {
        return;
      }

      isGenerateButtonClicked.current = false;

      const { text: characterLimitationText, count: characterLimitationCount } = pageConfig.character_limitation;

      if (!append && text.length > characterLimitationCount) {
        handleError(new PromptError(characterLimitationText), () => setAppState(State.OPTIONS));
        return;
      }

      const { batch_size: IMAGE_COUNT_PER_REQUEST } = serviceConfig;

      setAppState(images.length > 0 ? State.MORE_RESULTS : State.WAITING_RESULTS);

      const pendingImages = Array.from({
        length: IMAGE_COUNT_PER_REQUEST,
      }).map(
        (_, i) =>
          ({
            id: `pending-${i + (append ? images.length : 0)}`,
          } as any),
      );

      // Add the pending images 'indicators'
      setImages(oldImages => (append ? [...oldImages, ...pendingImages] : pendingImages));

      let requestSuccessful = false;

      try {
        setAppState(State.RESULTS);
        const colorText = colors.join(', ');
        // TODO
        const textToSend = [colorText, text, style?.prompt].reduce((acc, item) => {
          if (!item) return acc;
          if (!acc) return item;
          return `${acc}, ${item}`;
        }, 'Background')!;

        const response = await requestTextToBG(textToSend, IMAGE_COUNT_PER_REQUEST, pageConfig, serviceConfig);
        const responseImages = await getTaskResult(serviceConfig, response.prompt?.id, response.id);
        setPromptId(response.prompt?.id);

        // Remove the pending images 'indicators' and add results
        setImages(oldImages => (append ? [...oldImages.filter(i => !i.id.startsWith('pending-')), ...responseImages] : responseImages));

        requestSuccessful = true;
      } catch (e: any) {
        // Remove pending images
        setImages(oldImages => oldImages.filter(i => !i.id.startsWith('pending-')));

        handleError(e, () => setAppState(append ? State.RESULTS : State.OPTIONS));

        return;
      }

      // Go back to results screen only when we are still waiting for results
      // so we don't exit for example the details screen on mobile
      setAppState(appstate => (appstate === State.WAITING_RESULTS ? State.RESULTS : appstate));

      // Update the allowance status
      if (requestSuccessful && DAILY_ALLOWANCE > 0) {
        decreaseAllowance(context);
        setLimitReached(allowanceReached(context));
      }
    },
    [limitReached, pageConfig, text, serviceConfig, images.length, handleError, colors, style?.prompt, context],
  );

  const handleEnterKey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter' && textAreaRef.current) {
      textAreaRef.current.blur();
    }
  }, []);

  const handleGenerateClick = useCallback(async () => {
    state[termsOfServicesKey] ||= [];
    state[termsOfServicesKey].push(new Date().getTime());
    await setState(state, { permanent: true });
    if (!getContext().general.auth.getState().isAuthorized) {
      isGenerateButtonClicked.current = true;
      await publishRegistrationEvent();
    } else {
      await loadImages(false);
    }
  }, [loadImages, setState, state, termsOfServicesKey]);

  useEffect(() => {
    return getContext().general.auth.subscribe(async () => {
      if (getContext().general.auth.getState().isAuthorized && isGenerateButtonClicked.current) {
        await loadImages(false);
      }

      isGenerateButtonClicked.current = false;
    });
  }, [loadImages]);

  const handleInputClear = useCallback(() => {
    setPromptError(null);
    setText('');
  }, []);

  const handleInputChange = useCallback((event: SyntheticEvent<HTMLTextAreaElement>) => {
    setPromptError(null);
    setText((event.target as HTMLTextAreaElement).value);
  }, []);

  const onResultImageSelect = useCallback(
    async (img: ResultImage[]) => {
      try {
        await addImageToCanvas(img);
      } catch (e: any) {
        handleError(e, () => setAppState(State.RESULTS));
      }
    },
    [addImageToCanvas, handleError],
  );

  const RightAction = useMemo(() => {
    // TODO: Grdon alert
    const Element = text ? <InlineButton centerIcon={IconCrossCircleFilled} onClick={handleInputClear} /> : <></>;
    return [Element];
  }, [text, handleInputClear]);

  const handleStyleRemove = useCallback(() => setStyle(null), []);
  const handleColorsRemove = useCallback(() => setColors([]), []);
  const handleColorsChange = useCallback(
    (suggestedColor: string) => {
      const isSelected = colors.includes(suggestedColor);
      const updatedColors = isSelected ? colors.filter(color => color !== suggestedColor) : [...colors, suggestedColor];
      setColors(updatedColors);
    },
    [colors],
  );

  const handleKeywordClick = useCallback((keyword: string) => {
    setText(prevText => `${prevText}${prevText && ', '}${keyword}`);
  }, []);

  if (appState === State.RESULTS || appState === State.MORE_RESULTS) {
    return (
      <>
        {error && (
          <View className={classes.resultErrorContent}>
            <Error
              text={error}
              isBorder
              cb={() => {
                setError(null);
              }}
            />
          </View>
        )}

        <Results
          pageConfig={resultPageConfig}
          images={images}
          onSelect={onResultImageSelect}
          edit={edit}
          loadMore={() => loadImages(true)}
          onBackClick={() => setAppState(State.OPTIONS)}
          text={text}
          limitReached={limitReached}
        />
      </>
    );
  }

  return (
    <View className={classes.mainContent} style={{ height: mainHeight }}>
      {error && (
        <View className={classes.errorContent}>
          <Error
            text={error}
            isBorder
            cb={() => {
              setAppState(State.OPTIONS);
              setError(null);
            }}
          />
        </View>
      )}

      <Layout>
        <Header>
          {/* Header */}
          <View marginBlock={Spacings.s12} alignItems="center" gap={Spacings.s12}>
            <IconCrossLarge data-testId="closeIcon" className={classes.closeIcon} onClick={() => getContext().handlers.close()} />

            <Text size={Typography.t6} color={Texts.base} weight={FontWeights.Bold}>
              {pageConfig.title}
            </Text>
          </View>
          <View marginBlockEnd={Spacings.s8}>
            <Text size={Typography.t4} color={Texts.base} weight={FontWeights.Bold}>
              {pageConfig.description}
            </Text>
          </View>

          <TextArea
            status={promptError !== null ? TextAreaStatuses.Error : undefined}
            placeholder={pageConfig.placeholder_text}
            onChange={handleInputChange}
            onKeyDown={handleEnterKey}
            rightActions={RightAction}
            ref={textAreaRef}
            value={text}
            size={ControlSizesXL}
            dataTestId="promptArea"
          />

          {promptError && (
            <View marginBlockStart={Spacings.s8}>
              {text === pageConfig.nsfw_stop_info ? (
                <>
                  <IconInfoCircleOutline fill={Statuses.error.base.default} width={60} height={16} style={{ marginRight: Spacings.s6 }} />

                  <Text size={Typography.t2} color={Texts.base} weight={FontWeights.Regular} dangerouslySetInnerHTML={{ __html: promptError }} />
                </>
              ) : (
                <Text size={Typography.t4} color={Statuses.error.base.default} weight={FontWeights.Regular}>
                  {promptError}
                </Text>
              )}
            </View>
          )}
        </Header>

        <Body>
          {/* Try These */}
          <Grid columns="auto 1fr" gap={Spacings.s6} marginBlockStart={Spacings.s12}>
            <View>
              <Text size={Typography.t4} weight={FontWeights.Medium} color={Texts.base} preventNextLine className={classes.text}>
                Try these:
              </Text>
            </View>
            <ScrollBox className={classes.scrollbox} variant={ScrollBoxVariants.X} height="auto" width="auto">
              <View gap={Spacings.s8}>
                {pageConfig.bg_keywords.map((keyword: string) => (
                  <Button dataTestId="keywordButton" className={classes.button} key={keyword} name={keyword} size={ButtonSizeSM} skin={ButtonSkinTertiary} onClick={() => handleKeywordClick(keyword)}>
                    <Text size={Typography.t4} weight={FontWeights.SemiBold} color={Texts.base} preventNextLine>
                      {keyword}
                    </Text>
                  </Button>
                ))}
              </View>
            </ScrollBox>
          </Grid>

          {/* Color Header */}
          <View marginBlockStart={Spacings.s16} marginBlockEnd={Spacings.s12} justifyContent="space-between" alignItems="center">
            <Text size={Typography.t4} color={Texts.base} weight={FontWeights.SemiBold}>
              {pageConfig.select_color_text}
            </Text>
            <Button dataTestId="resetColorButton" isDisabled={!colors.length} size={ButtonSizeSM} skin={ButtonSkinSecondary} variant={ButtonVariants.Text} onClick={handleColorsRemove}>
              {pageConfig.reset_button_text}
            </Button>
          </View>

          {/* Color */}
          <Grid columns="repeat(8, 1fr)" gap={Spacings.s6}>
            {Object.entries(pageConfig.colors).map(([colorName, hex]) => {
              return <Pigment data-testId={colorName} key={colorName} isActive={colors.includes(colorName)} skin={hex as string} onClick={() => handleColorsChange(colorName)} />;
            })}
          </Grid>

          {/* Styles Header */}
          <View marginBlockStart={Spacings.s16} marginBlockEnd={Spacings.s12} justifyContent="space-between" alignItems="center">
            <Text size={Typography.t4} color={Texts.base} weight={FontWeights.SemiBold}>
              {pageConfig.select_style_text}
            </Text>
            <Button dataTestId="styleremoveButton" isDisabled={!style} size={ButtonSizeSM} skin={ButtonSkinSecondary} variant={ButtonVariants.Text} onClick={handleStyleRemove}>
              {pageConfig.reset_button_text}
            </Button>
          </View>

          {/* Styles */}
          <Grid columns="1fr 1fr 1fr 1fr" gap={Spacings.s6} marginBlockStart={Spacings.s12}>
            {pageConfig.styles.map((styleConf: IStyle) => (
              <View
                key={styleConf.id + styleConf.title}
                style={{ cursor: 'pointer' }}
                justifyContent="flex-start"
                flexDirection="column"
                alignItems="center"
                gap={Spacings.s4}
                onClick={() => {
                  setStyle(style?.id === styleConf.id ? null : styleConf);
                }}
              >
                <ImageFrame dataTestId={style?.id} width={70} height={70} isActive={style?.id === styleConf.id}>
                  <Image dataTestId="style" src={styleConf.thumbnail} alt={styleConf.title} />
                </ImageFrame>

                <Text size={Typography.t3} align={TypographyHorizontalAlign.Center} weight={FontWeights.Medium}>
                  {styleConf.title}
                </Text>
              </View>
            ))}
          </Grid>
        </Body>

        <Footer>
          {limitReached && (
            <Text size={Typography.t4} weight={FontWeights.Bold} align={TypographyHorizontalAlign.Center} color={Statuses.error.base.default}>
              {free.title}
            </Text>
          )}
          {limitReached && (
            <Text size={Typography.t4} weight={FontWeights.Regular} align={TypographyHorizontalAlign.Center} color={Statuses.error.base.default}>
              {free.description}
            </Text>
          )}
          {getContext().handlers.isWeb() && isTermsOfUseShown && (
            <View marginBlock={Spacings.s16}>
              <Text align={TypographyHorizontalAlign.Center} color={Texts.tint1} weight={FontWeights.SemiBold} size={Typography.t3}>
                {translate(pageConfig.agreement_text)}
                <Link weight={FontWeights.SemiBold} target="_blank" href="https://picsart.com/genaitermsofuse" size={Typography.t3}>
                  {translate(pageConfig.agreement_link)}
                </Link>
              </Text>
            </View>
          )}
          <View marginBlock={Spacings.s16}>
            <Button
              isDisabled={limitReached || appState === State.WAITING_RESULTS || (!style && !text.trim().length && !colors.length)}
              onClick={handleGenerateClick}
              variant={ButtonVariants.Filled}
              dataTestId="generateImageBtn"
              skin={ButtonSkinPrimary}
              size={ButtonSizeLG}
              isFullWidth
            >
              {pageConfig.generate_button_text}
            </Button>
          </View>
        </Footer>
      </Layout>
    </View>
  );
}

export default Main;
